import { Moment } from 'moment-timezone';
import {
    DaycareAvailabilityDate,
    DaycareAvailabilityDropOffSlot,
    DaycareAvailabilityPickUpSlot,
    DaycareAvailabilityRoom
} from 'types/Availability';
import { Marketplace } from 'types/Marketplace';
import { OnlineBookingTime } from 'types/OnlineBooking';
import { Pet } from 'types/Pet';
import { RoomRuleTypeIds } from 'types/Room';
import { Service } from 'types/Service';
import {
    BusinessHours,
    getMarketplaceBusinessHoursForDate,
    timeNumberToMinutes
} from 'utils/marketplaceScheduleUtils';
import { convertAvailabilityRoomToRoom } from 'utils/room/availabilityRoomConverter';
import { getRoomUnitAssignmentsForPets } from 'utils/room/roomUnitAssignment';
import { parseTotalCountRule } from 'utils/room/roomRuleParsers';

const TIME_SLOT_INTERVAL_MINUTES = 30;

const formatTimeLabel = (hour: number, minute: number) => {
    const displayHours = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour < 12 ? 'am' : 'pm';

    return `${displayHours}:${minute.toString().padStart(2, '0')} ${ampm}`;
};

const getServiceWindowOverlap = (
    serviceWindow?: { start: number; end: number },
    businessHours?: BusinessHours | null
) => {
    if (!serviceWindow || !businessHours) {
        return null;
    }

    const windowStartMinutes = timeNumberToMinutes(serviceWindow.start);
    const windowEndMinutes = timeNumberToMinutes(serviceWindow.end);
    const businessOpenMinutes = timeNumberToMinutes(businessHours.open);
    const businessCloseMinutes = timeNumberToMinutes(businessHours.close);

    if (
        windowStartMinutes === null ||
        windowEndMinutes === null ||
        businessOpenMinutes === null ||
        businessCloseMinutes === null
    ) {
        return null;
    }

    const overlapStartMinutes = Math.max(windowStartMinutes, businessOpenMinutes);
    const overlapEndMinutes = Math.min(windowEndMinutes, businessCloseMinutes);

    if (overlapStartMinutes > overlapEndMinutes) {
        return null;
    }

    return {
        start: overlapStartMinutes,
        end: overlapEndMinutes
    };
};

const alignStartMinutes = (minutes: number) => {
    return Math.ceil(minutes / TIME_SLOT_INTERVAL_MINUTES) * TIME_SLOT_INTERVAL_MINUTES;
};

const alignEndMinutes = (minutes: number) => {
    return Math.floor(minutes / TIME_SLOT_INTERVAL_MINUTES) * TIME_SLOT_INTERVAL_MINUTES;
};

const buildTimeSlots = (startMinutes: number, endMinutes: number, intervalMinutes: number) => {
    const alignedStartMinutes = Math.ceil(startMinutes / intervalMinutes) * intervalMinutes;
    const alignedEndMinutes = Math.floor(endMinutes / intervalMinutes) * intervalMinutes;

    if (alignedStartMinutes > alignedEndMinutes) {
        return [];
    }

    const slots: Array<OnlineBookingTime> = [];

    for (
        let minutes = alignedStartMinutes;
        minutes <= alignedEndMinutes;
        minutes += intervalMinutes
    ) {
        const hour = Math.floor(minutes / 60);
        const minute = minutes % 60;

        slots.push({
            hour,
            minute,
            label: formatTimeLabel(hour, minute)
        });
    }

    return slots;
};

export const generateDaycareTimeSlots = (
    startTime: number,
    endTime: number,
    intervalMinutes = TIME_SLOT_INTERVAL_MINUTES
): Array<OnlineBookingTime> => {
    const startMinutes = timeNumberToMinutes(startTime);
    const endMinutes = timeNumberToMinutes(endTime);

    if (startMinutes === null || endMinutes === null || startMinutes > endMinutes) {
        return [];
    }

    return buildTimeSlots(startMinutes, endMinutes, intervalMinutes);
};

export const getAvailableDaycareTimeSlots = (
    marketplace: Marketplace,
    service: Service,
    date: Moment
) => {
    const businessHours = getMarketplaceBusinessHoursForDate(marketplace, date);

    if (!businessHours) {
        return {
            dropOffSlots: [],
            pickUpSlots: []
        };
    }

    const dropOffWindow = getServiceWindowOverlap(service.dropOffTime, businessHours);
    const pickUpWindow = getServiceWindowOverlap(service.pickUpTime, businessHours);

    return {
        dropOffSlots: dropOffWindow
            ? buildTimeSlots(
                  alignStartMinutes(dropOffWindow.start),
                  alignEndMinutes(dropOffWindow.end),
                  TIME_SLOT_INTERVAL_MINUTES
              )
            : [],
        pickUpSlots: pickUpWindow
            ? buildTimeSlots(
                  alignStartMinutes(pickUpWindow.start),
                  alignEndMinutes(pickUpWindow.end),
                  TIME_SLOT_INTERVAL_MINUTES
              )
            : []
    };
};

export const getTimeInMinutes = (time?: OnlineBookingTime) => {
    if (!time) {
        return null;
    }

    return time.hour * 60 + time.minute;
};

export const convertMomentToOnlineBookingTime = (datetime: Moment): OnlineBookingTime => {
    return {
        hour: datetime.hour(),
        minute: datetime.minute(),
        label: datetime.format('h:mm a')
    };
};

export const assignDaycareUnitsForPets = (
    rooms: Array<DaycareAvailabilityRoom>,
    pets: Array<Pet>
): Array<number> | null => {
    console.log('[daycareUtils] assignDaycareUnitsForPets:start', {
        pets: pets.map(pet => ({
            id: pet.id,
            name: pet.name,
            type: pet.type.id,
            size: pet.size.id,
            weight: pet.weight
        })),
        rooms: rooms.map(room => ({
            id: room.id,
            name: room.name,
            petTypeList: room.petTypeList,
            roomRules: room.roomRules.map(rule => ({
                id: rule.id,
                ruleTypeId: rule.ruleTypeId,
                ruleData: rule.ruleData
            })),
            roomUnits: room.roomUnits.map(roomUnit => ({
                id: roomUnit.id,
                name: roomUnit.name,
                available: roomUnit.available,
                occupancy: roomUnit.occupancy
            }))
        }))
    });

    for (const room of rooms) {
        const roomUnitIds = getRoomUnitAssignmentsForPets(
            convertAvailabilityRoomToRoom(room),
            pets
        );

        console.log('[daycareUtils] assignDaycareUnitsForPets:room-result', {
            roomId: room.id,
            roomName: room.name,
            roomUnitIds
        });

        if (roomUnitIds) {
            return roomUnitIds;
        }
    }

    console.warn('[daycareUtils] assignDaycareUnitsForPets:no-assignment', {
        pets: pets.map(pet => pet.id)
    });

    return null;
};

export const MAX_DAYCARE_DAYS = 14;

const dateKey = (date: Moment): string => date.format('YYYY-MM-DD');

const isSameClock = (datetime: Moment, time: OnlineBookingTime): boolean =>
    datetime.hour() === time.hour && datetime.minute() === time.minute;

export const getSharedDaycareDropOffSlots = (
    availabilities: Array<DaycareAvailabilityDate>
): Array<DaycareAvailabilityDropOffSlot> => {
    if (availabilities.length === 0) {
        return [];
    }

    const [first, ...rest] = availabilities;

    return first.dropOffSlots.filter(slot => {
        const time = convertMomentToOnlineBookingTime(slot.datetime);

        return rest.every(availability =>
            availability.dropOffSlots.some(other => isSameClock(other.datetime, time))
        );
    });
};

export const getSharedDaycarePickUpSlots = (
    availabilities: Array<DaycareAvailabilityDate>,
    dropOffTime: OnlineBookingTime
): Array<DaycareAvailabilityPickUpSlot> => {
    if (availabilities.length === 0) {
        return [];
    }

    const dropOffPerDate = availabilities.map(availability =>
        availability.dropOffSlots.find(slot => isSameClock(slot.datetime, dropOffTime))
    );

    if (dropOffPerDate.some(slot => !slot)) {
        return [];
    }

    const [first, ...rest] = dropOffPerDate as Array<DaycareAvailabilityDropOffSlot>;

    return first.pickUpSlots.filter(pickUp => {
        const time = convertMomentToOnlineBookingTime(pickUp.datetime);

        return rest.every(dropOff =>
            dropOff.pickUpSlots.some(other => isSameClock(other.datetime, time))
        );
    });
};

export const assignDaycareUnitsForPetsAcrossDates = (
    availabilities: Array<DaycareAvailabilityDate>,
    dropOffTime: OnlineBookingTime,
    pickUpTime: OnlineBookingTime,
    pets: Array<Pet>
): Record<string, Array<number>> | null => {
    const assignmentsByDate: Record<string, Array<number>> = {};

    for (const availability of availabilities) {
        const dropOff = availability.dropOffSlots.find(slot =>
            isSameClock(slot.datetime, dropOffTime)
        );
        const pickUp = dropOff?.pickUpSlots.find(slot => isSameClock(slot.datetime, pickUpTime));

        if (!pickUp) {
            return null;
        }

        const roomUnitIds = assignDaycareUnitsForPets(pickUp.rooms, pets);

        if (!roomUnitIds) {
            return null;
        }

        assignmentsByDate[dateKey(availability.date)] = roomUnitIds;
    }

    return assignmentsByDate;
};

const getTotalCountCapacity = (room: DaycareAvailabilityRoom) => {
    const totalCountRule = room.roomRules.find(
        rule => rule.ruleTypeId === RoomRuleTypeIds.TotalCount
    );

    if (!totalCountRule) {
        return null;
    }

    const parsed = parseTotalCountRule(totalCountRule.ruleData);

    return parsed?.quantity ?? null;
};

const assignDaycareUnitsByAvailableUnits = (
    rooms: Array<DaycareAvailabilityRoom>,
    petsCount: number
): Array<number> | null => {
    const availableUnits = rooms
        .flatMap(room => {
            return room.roomUnits
                .filter(roomUnit => roomUnit.available)
                .map(roomUnit => ({
                    roomId: room.id,
                    unitId: roomUnit.id,
                    occupancy: roomUnit.occupancy
                }));
        })
        .sort((left, right) => {
            if (left.occupancy !== right.occupancy) {
                return left.occupancy - right.occupancy;
            }

            if (left.roomId !== right.roomId) {
                return left.roomId - right.roomId;
            }

            return left.unitId - right.unitId;
        });

    if (petsCount <= 0 || availableUnits.length < petsCount) {
        return null;
    }

    return availableUnits.slice(0, petsCount).map(unit => unit.unitId);
};

export const assignDaycareUnitsByTotalCount = (
    rooms: Array<DaycareAvailabilityRoom>,
    petsCount: number
): Array<number> | null => {
    const hasTotalCountCapacity = rooms.some(room => {
        const capacity = getTotalCountCapacity(room);

        return capacity !== null && capacity > 0;
    });

    console.log('[daycareUtils] assignDaycareUnitsByTotalCount:start', {
        petsCount,
        hasTotalCountCapacity,
        rooms: rooms.map(room => ({
            id: room.id,
            name: room.name,
            totalCountCapacity: getTotalCountCapacity(room),
            roomRules: room.roomRules.map(rule => ({
                id: rule.id,
                ruleTypeId: rule.ruleTypeId,
                ruleData: rule.ruleData
            })),
            roomUnits: room.roomUnits.map(roomUnit => ({
                id: roomUnit.id,
                name: roomUnit.name,
                available: roomUnit.available,
                occupancy: roomUnit.occupancy
            }))
        }))
    });

    if (!hasTotalCountCapacity) {
        const fallbackAssignments = assignDaycareUnitsByAvailableUnits(rooms, petsCount);

        console.log('[daycareUtils] assignDaycareUnitsByTotalCount:fallback-available-units', {
            petsCount,
            fallbackAssignments
        });

        return fallbackAssignments;
    }

    const candidates = rooms.flatMap(room => {
        const capacity = getTotalCountCapacity(room);

        if (capacity === null || capacity <= 0) {
            return [];
        }

        return room.roomUnits
            .filter(roomUnit => roomUnit.available)
            .map(roomUnit => ({
                roomId: room.id,
                unitId: roomUnit.id,
                occupancy: roomUnit.occupancy,
                remainingCapacity: capacity - roomUnit.occupancy
            }))
            .filter(candidate => candidate.remainingCapacity > 0);
    });

    const totalRemainingCapacity = candidates.reduce(
        (capacitySum, candidate) => capacitySum + candidate.remainingCapacity,
        0
    );

    console.log('[daycareUtils] assignDaycareUnitsByTotalCount:candidates', {
        petsCount,
        candidates,
        totalRemainingCapacity
    });

    if (petsCount <= 0 || totalRemainingCapacity < petsCount) {
        console.warn('[daycareUtils] assignDaycareUnitsByTotalCount:insufficient-capacity', {
            petsCount,
            totalRemainingCapacity
        });

        return null;
    }

    const sortCandidates = () => {
        candidates.sort((left, right) => {
            if (left.occupancy !== right.occupancy) {
                return left.occupancy - right.occupancy;
            }

            if (left.roomId !== right.roomId) {
                return left.roomId - right.roomId;
            }

            return left.unitId - right.unitId;
        });
    };

    const assignments: Array<number> = [];

    sortCandidates();

    for (const candidate of candidates) {
        if (assignments.length === petsCount) {
            break;
        }

        if (candidate.remainingCapacity <= 0) {
            continue;
        }

        assignments.push(candidate.unitId);
        candidate.remainingCapacity -= 1;
        candidate.occupancy += 1;
    }

    while (assignments.length < petsCount) {
        sortCandidates();

        const candidate = candidates.find(item => item.remainingCapacity > 0);

        if (!candidate) {
            console.warn('[daycareUtils] assignDaycareUnitsByTotalCount:no-candidate', {
                petsCount,
                assignments,
                candidates
            });

            return null;
        }

        assignments.push(candidate.unitId);
        candidate.remainingCapacity -= 1;
        candidate.occupancy += 1;
    }

    console.log('[daycareUtils] assignDaycareUnitsByTotalCount:success', {
        petsCount,
        assignments
    });

    return assignments;
};

export const hasValidDaycareTimesForDate = (
    marketplace: Marketplace,
    service: Service,
    date: Moment
) => {
    const { dropOffSlots, pickUpSlots } = getAvailableDaycareTimeSlots(marketplace, service, date);

    return dropOffSlots.some(dropOffTime => {
        const dropOffMinutes = getTimeInMinutes(dropOffTime);

        return pickUpSlots.some(pickUpTime => {
            const pickUpMinutes = getTimeInMinutes(pickUpTime);

            return (
                dropOffMinutes !== null && pickUpMinutes !== null && pickUpMinutes > dropOffMinutes
            );
        });
    });
};

export interface DaycareServiceRoomUnitAssignment {
    roomId: number;
    roomUnitIds: Array<number>;
}

export const getAssignedDaycareRooms = (service: Service) => {
    const selectedRoomUnitIds = new Set(service.daycareMetadata?.selectedRooms ?? []);

    if (!service.rooms?.length || selectedRoomUnitIds.size === 0) {
        return [];
    }

    return service.rooms
        .map(room => ({
            ...room,
            room_units: room.room_units.filter(
                roomUnit => roomUnit.active && selectedRoomUnitIds.has(roomUnit.id)
            )
        }))
        .filter(room => room.room_units.length > 0);
};

export const getDaycareServiceRoomUnitAssignment = (
    service: Service,
    pets: Array<Pet>
): DaycareServiceRoomUnitAssignment | null => {
    if (!service.daycareMetadata?.roomsEnabled || pets.length === 0) {
        return null;
    }

    if (!pets.every(pet => service.petList.includes(pet.type.id))) {
        return null;
    }

    const assignedRooms = getAssignedDaycareRooms(service);

    for (const room of assignedRooms) {
        const roomUnitIds = getRoomUnitAssignmentsForPets(room, pets);

        if (roomUnitIds) {
            return {
                roomId: room.id,
                roomUnitIds
            };
        }
    }

    return null;
};

export const isDaycareServiceAvailableForPets = (service: Service, pets: Array<Pet>) => {
    return getDaycareServiceRoomUnitAssignment(service, pets) !== null;
};

export const calculateDaycareHours = (
    dropOffTime: OnlineBookingTime,
    pickUpTime: OnlineBookingTime
): number => {
    const dropOffMinutes = dropOffTime.hour * 60 + dropOffTime.minute;
    const pickUpMinutes = pickUpTime.hour * 60 + pickUpTime.minute;

    return Math.ceil((pickUpMinutes - dropOffMinutes) / 60);
};
